import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextLink from 'LuxKit/TextLink'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { trackEvent } from 'analytics/analytics'
import { propertyRateImpressionEventWithContext } from 'analytics/snowplow/events'
import AccommodationSameRoomOptionTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import AccommodationSameRoomOptionTileCTA from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileCTA'
import AccommodationSameRoomOptionTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileDetails'
import AccommodationSameRoomOptionTileHeaderArea from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileHeaderArea'
import AccommodationSameRoomOptionTilePricing from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTilePricing'
import { AccommodationSameRoomOptionTileSubgrid } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileSubgrid'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import AgentExclusivePackageLabel from 'components/Common/Labels/AgentExclusivePackageLabel'
import Clickable from 'components/Common/Clickable'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import PayLaterPill from 'components/Common/PayLaterPill'
import type { PriceDetailsModalOfferProps } from 'components/Common/PriceDetails/PriceDetailsModal'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import ReserveForZeroPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PackageInclusionsValidityText, { hasStructuredPackageInclusionsValidityText } from 'components/OfferPage/Accommodation/PackageInclusionsValidityText'
import { getInclusionsWithoutPackageValidityText } from 'components/OfferPage/Accommodation/packageInclusionValidityUtils'
import {
  getPropertyRatePackageUpgradeSelectedKey,
  getPropertyRatePackageUpgradeTarget,
} from 'components/OfferPage/offerPageUtils/propertyLoyaltyPackageUpgrade'
import PriceRowMarginText from 'components/OfferPage/PriceRowMarginText'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { CHECKOUT_ITEM_TYPE_BEDBANK, CHECKOUT_ITEM_TYPE_EXPERIENCE, CHECKOUT_ITEM_TYPE_FLIGHT, CHECKOUT_ITEM_TYPE_LE_HOTEL } from 'constants/checkout'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { SOURCE_TYPES } from 'constants/source'
import ModalContext from 'contexts/ModalContext'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import type { OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import RateImpressionContext from 'contexts/RateImpressionContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { getPopularForOccupantsBannerMessage } from 'hooks/OfferPage/useSuggestedPackageOptionFromTotalSales'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useExperiencesPassportForOffer from 'components/Experiences/ExperiencesPassport/useExperiencesPassportForOffer'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import useGuestCheckoutEligibility from 'hooks/useGuestCheckoutEligibility'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import useMetasearchPriceId from 'hooks/useMetasearchPriceId'
import useMetasearchRuleId from 'hooks/useMetasearchRuleId'
import OfferPageBurnPriceDisplay from 'components/OfferPage/Loyalty/OfferPageBurnPriceDisplay'
import { isOfferPageFullyPaidWithPoints } from 'lib/offer/isOfferPageFullyPaidWithPoints'
import OfferPagePaidEntirelyWithPointsLabel from 'components/OfferPage/Loyalty/OfferPagePaidEntirelyWithPointsLabel'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { isLpcPropertyRateEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import { createPropertyRateLoyaltyUpgrade } from 'lib/checkout/accommodation/cart'
import RateTierProgressionCallOutBanner from 'loyalty/components/Banners/RateTierProgressionCallOutBanner'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import useHotelTierProgressionEligibility from 'loyalty/hooks/useHotelTierProgressionEligibility'
import usePropertyRateStatusCreditsCalculation from 'loyalty/hooks/usePropertyRateStatusCreditsCalculation'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import {
  generateLoyaltyPointsCalculatorOptInBundleOptions,
  generateLoyaltyPointsCalculatorPropertyBundledFlightOptions,
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import useLuxPlusPrimaryCashPrice from 'luxPlus/hooks/useLuxPlusPrimaryCashPrice'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import { arePropertyRatesLoading, getAgentHubBedbankPackagesForProperty, getPropertyCheapestHotelOnlyRateForDiscount, getPropertyOfferMapping } from 'selectors/offerPage/offerPageSelectors'
import { getSource, getSourceForProperty } from 'selectors/offerSelectors'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import useOfferPageBurnPoints from 'hooks/useOfferPageBurnPoints'

import PropertyAvailableRateOptionCtas from './PropertyAvailableRateOptionCtas'
import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyLoyaltyPackageUpgradeDebug from './PropertyLoyaltyPackageUpgradeDebug'
import PropertyOptionalBundleOption from './PropertyOptionalBundleOption'
import { getSelectedOptionalBundlesPrice, resolveSelectedOptionalBundles } from './propertyMultiRoomOptionalBundles'
import PropertyPopularRateBanner from './PropertyPopularRateBanner'
import PropertyPayAtPropertyModal from './PropertyPayAtPropertyModal'
import PropertyPromotionDetails from './PropertyPromotionDetails'
import PropertyPromotionLabel from './PropertyPromotionLabel'
import PropertyRateInclusions from './PropertyRateInclusions'
import PropertyRateOptionPrice from './PropertyRateOptionPrice'
import PropertyUnavailableRateOptionCtas from './PropertyUnavailableRateOptionCtas'
import { propertySingleRoomBookNowOrAddToCartCallback } from './propertyBuyCallback'
import { usePropertyOptionalBundleSelection } from './usePropertyOptionalBundleSelection'
import { getPropertyRateImpressionKey } from './propertyRateImpressionUtils'
import { getPayAtPropertyDetails, getPropertyRatePassportOffer } from './singlePropertyUtils'
import useInvalidateStalePrefetchedFlightsFromOffer from '../hooks/prefetchFlights/useInvalidateStalePrefetchedFlightsFromOffer'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import { isRestrictedPropertyPolicyEligibleRate } from 'lib/offer/cancellationPolicyUtils'

const SuggestedPackageBannerContainer = styled.div`
  ${mediaQueryUp.tablet} {
    border-inline: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const RefundabilityInclusionsSpacing = styled.div`
  padding-top: ${rem(8)};
`

function RefundabilityInclusionsGap(props: { enabled: boolean, children: React.ReactNode }) {
  const { enabled, children } = props

  if (!enabled) {
    return <>{children}</>
  }

  return <RefundabilityInclusionsSpacing>{children}</RefundabilityInclusionsSpacing>
}

interface Props {
  'data-testid'?: string
  property: App.Property.Property
  isPropertyUltraLux: boolean
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  lpcOffer: App.HotelOffer | undefined
  rate: App.EnrichedPropertyRate
  originAirport: App.Airport | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  duration: number
  rooms: Array<App.Occupants>
  regionCode: string
  source: string
  utmContent: string | undefined
  roomOption: OfferPageRoomOption | undefined
  isLoggedIn: boolean
  isSpoofed: boolean
  subscriber: App.OrderSubscriber | undefined
  canShowMargin: boolean
  canShowStaffLabel: boolean
  isInclusionsValidityEnabled: boolean
  cheapestHotelOnlyDiscountRate: App.EnrichedPropertyRate | undefined
  agentHubBedbankPackages: Array<{ packageId: string; mx: string }>
  // TODO analytics
  roomListId: string
  roomPosition: number // TODO one-indexed
  rateListId: string
  ratePosition: number
  /** Hide the tier progression banner (used when rendered inside PropertyRateOptionMerged) */
  hideTierProgressionBanner?: boolean
  cancellationPolicyContentOverride?: React.ReactNode
  loyaltyTargetStayUpgradesSelected: App.LoyaltyTargetStayUpgradesSelected | undefined
  loyaltyUpgradeBanner?: React.ReactNode
  packageUpgradeTargetRate?: App.EnrichedPropertyRate
}

function PropertyRateOption(props: Props) {
  const {
    property,
    isPropertyUltraLux,
    lpcOffer,
    rate,
    originAirport,
    checkIn,
    checkOut,
    duration,
    rooms,
    regionCode,
    source,
    utmContent,
    roomOption,
    isLoggedIn,
    isSpoofed,
    subscriber,
    canShowMargin,
    canShowStaffLabel,
    isInclusionsValidityEnabled,
    cheapestHotelOnlyDiscountRate,
    agentHubBedbankPackages,
    roomListId,
    roomPosition,
    rateListId,
    ratePosition,
    hideTierProgressionBanner,
    cancellationPolicyContentOverride,
    loyaltyTargetStayUpgradesSelected,
    loyaltyUpgradeBanner,
    packageUpgradeTargetRate,
  } = props

  const passportOffer = useMemo(
    () => getPropertyRatePassportOffer(rate, property),
    [property, rate],
  )
  // A guest's GHA voucher is minted after navigation to checkout, where the route no longer
  // identifies this property's CUG source. Resolve it while the property context is available.
  const voucherSource = useAppSelector(state =>
    rate.service.type === 'bedbank' ? getSourceForProperty(state, rate.service.propertyId) : undefined,
  )

  const loyaltyTargetUpgradeRate = getPropertyRatePackageUpgradeTarget(rate)
  const loyaltyPackageUpgradeSelectedKey = useMemo(() => getPropertyRatePackageUpgradeSelectedKey(rate), [rate])
  const isLoyaltyPackageUpgradeSelected = loyaltyPackageUpgradeSelectedKey ? loyaltyTargetStayUpgradesSelected?.[loyaltyPackageUpgradeSelectedKey] === 'package' : false
  const isLoyaltyPackageUpgradeApplied = !!(isLoyaltyPackageUpgradeSelected && loyaltyTargetUpgradeRate)
  // Applying a package upgrade swaps the tile's content (inclusions/cancellation) to the upgraded
  // target rate, while pricing stays on the source rate
  const displayedContentRate = isLoyaltyPackageUpgradeApplied && loyaltyTargetUpgradeRate
    ? loyaltyTargetUpgradeRate
    : rate

  const loyaltyTargetUpgradeRoomImages = loyaltyTargetUpgradeRate ?
    property.rooms[loyaltyTargetUpgradeRate.roomId]?.images :
    undefined

  const loyaltyProductType: App.LoyaltyAccommodationProductType = rate.service.type === 'bedbank' ? 'hotel_lpp_eps' : 'hotel_lpc'
  const burnResult = useOfferPageBurnPoints(rate.totals.displayPrice, loyaltyProductType)
  const { enabled: showPointsPrice, pointsToBurn: burnPointsToApply, priceInPoints: burnPriceInPoints, hideZeroCash: burnHideZeroCash } = burnResult
  const intl = useIntl()
  const showPointsSavings = isOfferPageFullyPaidWithPoints(
    burnPointsToApply,
    burnPriceInPoints,
    burnHideZeroCash,
  )
  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const offerPageState = useContext(OfferPageStateContext)
  const propertyRatesLoading = useAppSelector(appState => arePropertyRatesLoading(offerPageState, appState))
  const modalElementContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)
  const impressionContext = useContext(RateImpressionContext)
  const metasearchPriceId = useMetasearchPriceId()
  const metasearchRuleId = useMetasearchRuleId()
  const cheapestHotelOnlyRate = cheapestHotelOnlyDiscountRate
  const isMetasearchDiscountEligibleRate = (
    rate.service.type === 'bedbank' &&
    !rate.includesFlights &&
    cheapestHotelOnlyRate &&
    'service' in cheapestHotelOnlyRate &&
    cheapestHotelOnlyRate.service.type === 'bedbank' &&
    cheapestHotelOnlyRate.service.propertyId === rate.service.propertyId &&
    cheapestHotelOnlyRate.rateId === rate.rateId
  )
  const [discountAmount, discountFetching, metasearchDiscountSettled] = useMetasearchDiscount(rate.totals.displayPrice, {
    propertyId: rate.service.type === 'bedbank' ? rate.service.propertyId : undefined,
    checkIn,
    checkOut,
    rooms,
    disabled: !isMetasearchDiscountEligibleRate,
  })
  const {
    selectedBundleIds: selectedOptionalBundleIds,
    onToggleBundle: onToggleOptionalBundle,
  } = usePropertyOptionalBundleSelection(rate.rateId)
  const selectedOptionalBundles = useMemo(
    () => resolveSelectedOptionalBundles(rate, selectedOptionalBundleIds),
    [rate, selectedOptionalBundleIds],
  )
  const selectedOptionalBundlesTotalPrice = getSelectedOptionalBundlesPrice(rate, selectedOptionalBundleIds)
  const optionalBundlesSection = <PropertyOptionalBundleOption
    optionalBundles={rate.optionalBundles}
    selectedBundleIds={selectedOptionalBundleIds}
    onToggleBundle={onToggleOptionalBundle}
  />
  const displayPrice = Math.max(rate.totals.displayPrice - discountAmount, 0)
  const displayMemberPrice = rate.luxPlusTotals?.displayPrice ? Math.max(rate.luxPlusTotals.displayPrice - discountAmount, 0) : undefined
  const displayedNonMemberPrice = displayPrice + selectedOptionalBundlesTotalPrice
  const displayedMemberPrice = displayMemberPrice === undefined ? undefined : displayMemberPrice + selectedOptionalBundlesTotalPrice
  const displayedValue = rate.totals.valuedUpToPrice + selectedOptionalBundlesTotalPrice
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const displayedCashPrice = useLuxPlusPrimaryCashPrice({
    price: displayedNonMemberPrice,
    memberPrice: displayedMemberPrice ?? 0,
  })

  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldBenefit = useMemo(() => {
    if (!isTasteOfGoldActive || !lpcOffer) return false
    return isTasteOfGoldEligibleOffer(
      lpcOffer,
      rate.currency,
      property.location.countryCode?.toLowerCase() !== 'au',
      rate.totals.sellingPrice,
    )
  }, [isTasteOfGoldActive, lpcOffer, rate.currency, rate.totals.sellingPrice, property.location.countryCode])

  const impressionKey = useMemo(() => getPropertyRateImpressionKey(rate, originAirport), [rate, originAirport])

  const canFireImpression = !!(
    roomListId &&
    roomPosition &&
    rateListId &&
    metasearchDiscountSettled &&
    !propertyRatesLoading &&
    impressionContext.isReady &&
    (!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0))
  )

  const [inViewRef, inView] = useInView({ skip: impressionContext.hasFired(impressionKey), threshold: 0.5 })

  const {
    checkoutSources: experiencesPassportCheckoutSources,
    fetching: experiencesPassportFetching,
  } = useExperiencesPassportForOffer({ offer: passportOffer })

  // TODO `formattedShortDateByTimezone` includes a hack for string timestamps suffixed with `'T00:00:00.000Z'`, which we want to avoid
  const headlineCancellationPolicyStart = displayedContentRate.headlineCancellationPolicyStart ? new Date(displayedContentRate.headlineCancellationPolicyStart) : undefined
  const rawBedbankDiscount = useBedbankDiscount(rate.totals.discount, rate.luxPlusTotals?.discount ?? 0)
  // TODO(HP-1404): Show margin details for LuxPlus+ prices.
  const shouldShowMargin = !!(canShowMargin && rate.totals.margin && rate.totals.marginAud)
  const payAtPropertyDetailsForRate = getPayAtPropertyDetails(rate)
  // Pay-at-property applies to the hotel only -- the pay-at-property variant rates carry no
  // bundles, so choosing one silently drops the bundle from the booking: ticked opt-in
  // bundles vanish, and a mandatory-bundled rate switches to its unbundled sibling. Don't
  // offer the option (pill or modal) while either kind of bundle is in play; unticking an
  // opt-in brings it back.
  const payAtPropertyDetails = useMemo<typeof payAtPropertyDetailsForRate>(() => (
    selectedOptionalBundleIds.length > 0 || rate.bundle
      ? { shouldShowPill: false }
      : payAtPropertyDetailsForRate
  ), [selectedOptionalBundleIds, rate.bundle, payAtPropertyDetailsForRate])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      generateLoyaltyPointsCalculatorPropertyOptions(rate, {
        propertyCountryCode: property.location.countryCode,
        propertyStateProvince: property.location.region,
        currentRegionCode: regionCode,
        isUltraLux: isPropertyUltraLux,
      }),
      generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(rate),
      ...generateLoyaltyPointsCalculatorOptInBundleOptions(selectedOptionalBundles),
    ]
  }, [rate, property.location.countryCode, property.location.region, regionCode, isPropertyUltraLux, selectedOptionalBundles])

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isLoyaltyPackageUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const isLoyaltyPackageUpgradeFeatureEnabled = useAppSelector(
    state => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.loyaltySppPackageUpgrades],
  )
  const currentCurrency = useAppSelector(state => state.geo.currentCurrency)
  const airports = useAppSelector(state => state.geo.airports)
  const memberId = useAppSelector(state => state.auth.account.memberId)

  const { statusCredits: rateStatusCredits, isLoading: statusCreditsLoading } = usePropertyRateStatusCreditsCalculation({
    rate,
    propertyCountryCode: property.location.countryCode,
    propertyStateProvince: property.location.region,
    currentRegionCode: regionCode,
    isUltraLux: isPropertyUltraLux,
    nights: duration,
    skip: !isLoyaltyEnabled || !hasLoyaltyAccount,
  })

  const tierProgressionEligibility = useHotelTierProgressionEligibility({
    statusCredits: rateStatusCredits,
    nights: duration,
    checkoutDate: checkOut,
    skip: !isLoyaltyEnabled || !hasLoyaltyAccount || statusCreditsLoading,
  })

  const { showTierProgressionBanner, highestUnlockableTier, highestUnlockableTierName } = tierProgressionEligibility
  const shouldShowTierProgressionBanner = showTierProgressionBanner && !hideTierProgressionBanner
  const isLoyaltyPackageUpgradeEligible = !!(
    isLoyaltyEnabled &&
    isLoyaltyPackageUpgradesAvailable &&
    isCurrentRegionUserLoyaltyRegion &&
    loyaltyTargetUpgradeRate &&
    loyaltyPackageUpgradeSelectedKey &&
    isLoyaltyPackageUpgradeFeatureEnabled &&
    rooms.length === 1 // Package upgrades are only available for single-room bookings. Multi-room not supported for launch.
  )

  const inclusionsUpgradeTargetRate = isLoyaltyPackageUpgradeFeatureEnabled || isLoyaltyPackageUpgradeApplied
    ? loyaltyTargetUpgradeRate ?? packageUpgradeTargetRate
    : undefined
  // Built whenever the upgrade is *available*, not just when selected — an unselected upgrade rides
  // along on the cart item (`applied: false`) so checkout can offer it. Unselected availability
  // requires the banner to actually be shown; a selection carries regardless (matching the swapped
  // tile content, even if banner eligibility has since changed).
  const loyaltyPackageUpgrade = useMemo(() => {
    if (!loyaltyPackageUpgradeSelectedKey || !loyaltyTargetUpgradeRate) return
    if (!isLoyaltyPackageUpgradeApplied && !isLoyaltyPackageUpgradeEligible) return

    return {
      sourceRate: rate,
      targetRate: loyaltyTargetUpgradeRate,
      upgrade: createPropertyRateLoyaltyUpgrade(rate, loyaltyTargetUpgradeRate, isPropertyUltraLux, loyaltyTargetUpgradeRoomImages?.[0], isLoyaltyPackageUpgradeApplied),
    }
  }, [isLoyaltyPackageUpgradeEligible, isLoyaltyPackageUpgradeApplied, rate, loyaltyPackageUpgradeSelectedKey, loyaltyTargetUpgradeRate, isPropertyUltraLux, loyaltyTargetUpgradeRoomImages])

  useEffect(() => {
    if (!canFireImpression || !inView) return
    if (impressionContext.hasFired(impressionKey)) return

    trackEvent(propertyRateImpressionEventWithContext({
      occupancy: rooms,
      checkIn,
      checkOut,
      roomListId,
      roomPosition,
      rateListId,
      ratePosition,
      rate,
      displayPrice: displayedCashPrice,
      displayMemberPrice: displayedMemberPrice ?? displayedNonMemberPrice,
      displayNonMemberPrice: displayedNonMemberPrice,
      displayValue: displayedValue,
    }))
    impressionContext.markAsFired(impressionKey)
  }, [
    canFireImpression,
    checkIn,
    checkOut,
    impressionContext,
    impressionKey,
    inView,
    propertyRatesLoading,
    ratePosition,
    rate,
    rateListId,
    roomListId,
    roomPosition,
    rooms,
    displayedCashPrice,
    displayedMemberPrice,
    displayedNonMemberPrice,
    displayedValue,
  ])

  const invalidateStalePrefetchedFlightsFromOffer = useInvalidateStalePrefetchedFlightsFromOffer()
  const { canProceedAsGuest, activate: activateGuestCheckoutExperiment } = useGuestCheckoutEligibility(isLoggedIn)
  const guestCheckoutPurchase = useMemo<{
    isWalledGarden: boolean
    isMultiRoom: boolean
    itemTypes: Array<App.Checkout.AnyItemType>
  }>(() => ({
    isWalledGarden: !!lpcOffer?.walledGarden,
    isMultiRoom: rooms.length > 1,
    itemTypes: [
      ...rooms.map(() => (rate.service.type === 'bedbank' ? CHECKOUT_ITEM_TYPE_BEDBANK : CHECKOUT_ITEM_TYPE_LE_HOTEL) as App.Checkout.AnyItemType),
      ...(rate.includesFlights ? [CHECKOUT_ITEM_TYPE_FLIGHT as App.Checkout.AnyItemType] : []),
      ...(rate.bundle || selectedOptionalBundleIds.length > 0 ? [CHECKOUT_ITEM_TYPE_EXPERIENCE as App.Checkout.AnyItemType] : []),
      ...(experiencesPassportCheckoutSources.length ? [CHECKOUT_ITEM_TYPE_EXPERIENCE as App.Checkout.AnyItemType] : []),
    ],
  }), [experiencesPassportCheckoutSources.length, lpcOffer?.walledGarden, rate.bundle, rate.includesFlights, rate.service.type, rooms, selectedOptionalBundleIds])
  const onCtaClick = useCallback(async(shouldAddToCart: boolean) => {
    if (!(checkIn && checkOut)) throw new Error('Attempted to perform cart action when dates aren’t set')
    if (!shouldAddToCart) {
      invalidateStalePrefetchedFlightsFromOffer()
    }
    trackOfferPageEvent('BOOK_NOW_PRESSED')

    // TODO we don't care about walled garden specifics
    // After a successful login modal the Redux selector can still be stale in this closure.
    let bookedWhileLoggedIn = isLoggedIn
    const isGuestCheckout = !shouldAddToCart && canProceedAsGuest(guestCheckoutPurchase)
    if (!bookedWhileLoggedIn) {
      activateGuestCheckoutExperiment(guestCheckoutPurchase)
      if (!isGuestCheckout) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result?.loggedIn) {
          return
        }
        bookedWhileLoggedIn = true
      }
    }

    const chosenRate = (payAtPropertyDetails.shouldShowPill && payAtPropertyDetails.shouldShowModal)
      ? await showModal<App.EnrichedPropertyRate | undefined>(
          <PropertyPayAtPropertyModal
            propertyTimeZone={property.location.timezone}
            duration={duration}
            propertyCollectRate={payAtPropertyDetails.propertyCollectRate}
            leCollectRate={payAtPropertyDetails.leCollectRate}
            optInBundlesPrice={selectedOptionalBundlesTotalPrice}
          />,
        )
      : rate
    if (!chosenRate) return
    // The upgrade payload only belongs to this option's own rate (not e.g. a pay-at-property variant).
    const checkoutLoyaltyPackageUpgrade = chosenRate === rate ? loyaltyPackageUpgrade : undefined
    // Applied ⇒ book the target rate (charging the source); available-only ⇒ book the source as normal.
    const checkoutRate = checkoutLoyaltyPackageUpgrade?.upgrade.applied
      ? checkoutLoyaltyPackageUpgrade.targetRate
      : chosenRate

    propertySingleRoomBookNowOrAddToCartCallback({
      shouldAddToCart,
      dispatch,
      currentCurrency,
      airports,
      currentRegionCode: regionCode,
      memberId,
      offerPageDispatch,
      modalElementContext,
      showModal,
      intl,
      property,
      rate: checkoutRate,
      ratePosition,
      originAirportCode: originAirport?.code,
      checkIn,
      checkOut,
      duration,
      rooms,
      regionCode,
      source: source ? `${source},${SOURCE_TYPES.CTA}` : SOURCE_TYPES.CTA,
      utmContent,
      roomOption,
      isSpoofed,
      isLoggedIn: bookedWhileLoggedIn,
      isGuestCheckout,
      voucherSource,
      subscriber,
      roomListId,
      roomPosition,
      rateListId,
      loyalty: checkoutLoyaltyPackageUpgrade,
      metasearchPriceId,
      metasearchRuleId,
      shouldApplyDiscount: isMetasearchDiscountEligibleRate,
      experiencesPassportCheckoutSources,
      selectedOptionalBundleIds: chosenRate === rate && selectedOptionalBundleIds.length ? selectedOptionalBundleIds : undefined,
      isLuxPlusMember: canViewLuxPlusBenefits,
      displayMemberPrice: displayedMemberPrice ?? displayedNonMemberPrice,
      displayNonMemberPrice: displayedNonMemberPrice,
      displayValue: displayedValue,
    })
  }, [
    airports,
    activateGuestCheckoutExperiment,
    canProceedAsGuest,
    checkIn,
    checkOut,
    currentCurrency,
    dispatch,
    duration,
    invalidateStalePrefetchedFlightsFromOffer,
    intl,
    isLoggedIn,
    isSpoofed,
    memberId,
    modalElementContext,
    metasearchPriceId,
    metasearchRuleId,
    offerPageDispatch,
    originAirport?.code,
    payAtPropertyDetails,
    property,
    rate,
    rateListId,
    ratePosition,
    regionCode,
    roomListId,
    roomOption,
    roomPosition,
    rooms,
    isMetasearchDiscountEligibleRate,
    guestCheckoutPurchase,
    experiencesPassportCheckoutSources,
    showModal,
    source,
    subscriber,
    trackOfferPageEvent,
    utmContent,
    voucherSource,
    loyaltyPackageUpgrade,
    selectedOptionalBundleIds,
    selectedOptionalBundlesTotalPrice,
    canViewLuxPlusBenefits,
    displayedMemberPrice,
    displayedNonMemberPrice,
    displayedValue,
  ])

  const handleBookNowFromModal = useCallback(() => {
    onCtaClick(false)
  }, [onCtaClick])

  const handleAddToCartFromModal = useCallback(() => {
    onCtaClick(true)
  }, [onCtaClick])

  const onToggleFilter = useCallback((filter: 'airport' | 'checkin') => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch])
  const ctas = useMemo(() => <>
    {(!rate.includesFlights || typeof rate.cheapestFlightPrice === 'number') && <PropertyAvailableRateOptionCtas
      includesFlights={rate.includesFlights}
      isLoyaltyPackageUpgradeApplied={isLoyaltyPackageUpgradeApplied}
      disabled={experiencesPassportFetching}
      onCtaClick={onCtaClick}
    />}
    {rate.includesFlights && typeof rate.cheapestFlightPrice === 'string' && <PropertyUnavailableRateOptionCtas
      shouldChangeAirport={rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable'}
      shouldChangeDepartureDate={rate.cheapestFlightPrice === 'departureTooEarly' || rate.cheapestFlightPrice === 'departureTooLate'}
      onToggleFilter={onToggleFilter}
    />}
  </>, [experiencesPassportFetching, onCtaClick, onToggleFilter, rate, isLoyaltyPackageUpgradeApplied])

  const cancellationPolicy = <PropertyCancellationPolicy
    propertyTimeZone={property.location.timezone}
    headlineCancellationPolicyType={displayedContentRate.headlineCancellationPolicyType}
    restrictedPolicyEligible={isRestrictedPropertyPolicyEligibleRate(displayedContentRate)}
    headlineCancellationPolicyStart={headlineCancellationPolicyStart}
    fullyRefundableUntil={displayedContentRate.fullyRefundableUntil}
    cancellationPolicies={displayedContentRate.cancellationPolicies}
    currency={rate.currency}
    shouldClarifyRefundIsHotelOnly={rate.includesFlights}
  />

  const openPriceDetailsModal = useCallback(async() => {
    const priceDetailsModalOfferProps: PriceDetailsModalOfferProps = rate.service.type === 'lpc' ? {
      offerId: rate.service.offerId,
      offerType: OFFER_TYPE_ALWAYS_ON,
      saleUnit: 'room',
      displayPricingAsPerPerson: false,
    } : {
      offerId: rate.service.propertyId,
      offerType: OFFER_TYPE_BED_BANK,
      saleUnit: 'room',
      // TODO(HP-1614): Handle `bedbankOffer.displayPricingAsPerPerson`.
      displayPricingAsPerPerson: false,
    }
    const dueAtPropertyMessage = rate.service.type === 'lpc' ? property.taxesAndFeesContent : undefined
    // NB: This is used to narrow down the correct Agent Hub commission. However, it's unclear why
    // we don't do the same with package IDs for LPC rates.
    const rateId = rate.service.type === 'bedbank' ? rate.service.rateId : undefined

    // This implementation is based off `<OfferPriceDetailsRow/>`.
    // NB: Don't pass `flightPrice` to `<PriceDetailsModal/>`, as it's already baked in.
    const result = await showModal<'fine-print' | undefined>(<PriceDetailsModal
      {...priceDetailsModalOfferProps}
      offerPage
      duration={duration}
      taxesAndFees={rate.totals.taxesAndFees}
      propertyFees={rate.totals.propertyFees}
      hotelPrice={rate.totals.sellingPrice}
      hotelMemberPrice={rate.luxPlusTotals?.sellingPrice}
      showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
      rooms={rooms}
      dueAtPropertyMessage={dueAtPropertyMessage}
      cta={
        <Group direction="vertical" tabletDirection="horizontal-reverse" horizontalAlign="stretch" tabletHorizontalAlign="end" gap={8}>
          {ctas}
        </Group>
      }
      rateId={rateId}
      postTaxDiscount={discountAmount || undefined}
      isGhaVerifiedRate={isMetasearchDiscountEligibleRate}
      optInBundlesPrice={selectedOptionalBundlesTotalPrice}
    />)
    if (result === 'fine-print') {
      window.location.href = '#fine-print'
    }
  }, [ctas, discountAmount, duration, property, rate, rooms, showModal, selectedOptionalBundlesTotalPrice, isMetasearchDiscountEligibleRate])
  const taxesAndFeesContent = <Clickable onClick={openPriceDetailsModal}>
    <Caption variant="medium" colour="light-foreground-alt" underline>
      {intl.formatMessage({
        id: 'H29_x-SS95OszUi2OiUM',
        defaultMessage: 'Includes taxes & fees',
        description: 'Label indicating that taxes and fees are included in the price',
      })}
    </Caption>
  </Clickable>

  const noFlightsAvailableMessage = intl.formatMessage(
    {
      id: 'KCe0399qOgmK-1GZSQxq',
      defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
      description: 'Error message when no flights are available from the selected airport',
    },
    { airportName: originAirport?.name },
  )
  const flightsWithin24HoursMessage = intl.formatMessage({
    id: 'WwEhobNYMcqSErYgfeMM',
    defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours.',
    description: 'Error message when departure is too soon for online flight booking',
  })
  const flightsAdvanceBookingMessage = intl.formatMessage({
    id: '0j8ptAKnepQILSZ4qfTs',
    defaultMessage: 'Flights can only be booked up to 11 months in advance.',
    description: 'Error message when departure date is too far in advance for flight booking',
  })
  const addFlightsMessage = intl.formatMessage({
    id: 'OrJCJ4isDFtPDzbv6XYB',
    defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
    description: 'Info message about adding flights to the hotel package at checkout',
  })

  const strongCalloutMessage = useMemo(() => {
    if (rate.calloutReason === 'popular-occupancy') {
      const message = getPopularForOccupantsBannerMessage(intl, rooms)
      if (message) return message
      return intl.formatMessage({
        id: 'IYB1pLJaKfNI5CSqWM8f',
        defaultMessage: 'Recommended package',
        description: 'Promotional banner message shown above an uplifted rate',
      })
    }
  }, [intl, rate.calloutReason, rooms])
  const staffRateLabel = canShowStaffLabel && rate.staffLabel ? intl.formatMessage({
    id: 'HJnIuZ3JW8_X7JmL0chU',
    defaultMessage: '{staffLabel} (label only shows for staff)',
    description: 'Staff-only label providing additional rate details',
  }, { staffLabel: rate.staffLabel }) : undefined
  const lpcPackageId = rate.service.type === 'lpc' ? rate.service.packageId : undefined
  const matchingPackage = useMemo(() => {
    if (!lpcPackageId) {
      return undefined
    }

    return lpcOffer?.packages.find(pkg =>
      pkg.id === lpcPackageId || pkg.lePackageId === lpcPackageId,
    )
  }, [lpcOffer?.packages, lpcPackageId])
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const showReserveForZero = isSpoofed && isLpcPropertyRateEligibleForZeroReservation(lpcOffer, rate, checkInMoment)
  const openReserveForZeroPolicyModal = useCallback(() => {
    showModal(<ReserveForZeroPolicyModal />)
  }, [showModal])
  const hasPackageInclusions = (
    rate.luxPlusInclusions.length > 0 ||
    rate.bonusInclusions.length > 0 ||
    rate.bundleInclusions.length > 0 ||
    rate.inclusions.length > 0
  )
  const shouldShowInclusionsValidityText = rate.service.type === 'lpc' && isInclusionsValidityEnabled && !!matchingPackage?.showInclusionsValidityTag && hasPackageInclusions && hasStructuredPackageInclusionsValidityText(matchingPackage?.roomPolicyDescription)
  const visibleRateInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(rate.inclusions, shouldShowInclusionsValidityText)
  }, [rate.inclusions, shouldShowInclusionsValidityText])
  const visibleRateBundleInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(rate.bundleInclusions, shouldShowInclusionsValidityText)
  }, [rate.bundleInclusions, shouldShowInclusionsValidityText])
  const visibleRateBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(rate.bonusInclusions, shouldShowInclusionsValidityText)
  }, [rate.bonusInclusions, shouldShowInclusionsValidityText])

  /**
   * LPC RATES
   */
  if (rate.service.type === 'lpc') {
    const lpcDiscount = rate.totals.discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rate.totals.discount : 0
    const shouldShowValue = rate.totals.valuedUpToPrice > rate.totals.sellingPrice
    const selectedOccupancy = rooms.length === 1 ? rooms[0] : undefined
    const showAgentExclusivePackageLabel = (
      !!matchingPackage?.isAgentHubExclusive &&
      !lpcOffer?.isAgentHubExclusive &&
      !property.isAgentHubExclusive
    )

    const lpcDurationLabel = rate.includesFlights
      ? intl.formatMessage(
          {
            id: 'WlUcNFNb6HRC0YOSv57_',
            defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
            description: 'Duration with flights for LPC property rate option',
          },
          { count: duration },
        )
      : intl.formatMessage(
          {
            id: '-mSccfdQDlul78PcyBog',
            defaultMessage: '{count, plural, one {# night} other {# nights}}',
            description: 'Duration in nights for LPC property rate option on the form',
          },
          { count: duration },
        )

    return (
      <AccommodationSameRoomOptionTileSubgrid>
        {strongCalloutMessage && <SuggestedPackageBannerContainer>
          <PropertyPopularRateBanner message={strongCalloutMessage} />
        </SuggestedPackageBannerContainer>}
        <AccommodationSameRoomOptionTile
          data-testid={rate.service.rateId}
          ref={inViewRef}
          loyaltyUpgradeAppliedTier={isLoyaltyPackageUpgradeApplied ? tier : undefined}
        >
          {shouldShowTierProgressionBanner && highestUnlockableTier && (
            <AccommodationSameRoomOptionTileHeaderArea>
              <RateTierProgressionCallOutBanner
                highestUnlockableTier={highestUnlockableTier}
                highestUnlockableTierName={highestUnlockableTierName}
                onBookNow={handleBookNowFromModal}
                onAddToCart={handleAddToCartFromModal}
                checkoutDate={checkOut}
              />
            </AccommodationSameRoomOptionTileHeaderArea>
          )}
          {!!loyaltyUpgradeBanner && <AccommodationSameRoomOptionTileHeaderArea>
            {loyaltyUpgradeBanner}
          </AccommodationSameRoomOptionTileHeaderArea>}
          <AccommodationSameRoomOptionTileDetails
            optionName={rate.name}
            selectedLoyaltyTargetUpgrade={isLoyaltyPackageUpgradeApplied && loyaltyTargetUpgradeRate ? { name: loyaltyTargetUpgradeRate.name } : undefined}
            upgradeTitleVariant="packageUpgrade"
            tagLineLabel={<>
              {/* NB: Accommodation service synthesises a `promotion` for tactical rates. */}
              {displayedContentRate.promotion && <PropertyPromotionLabel promotion={displayedContentRate.promotion} />}
              {!displayedContentRate.promotion && rate.hasAppliedDiscount && <ProductTypeLabel productType="limited_time_special" />}
            </>}
          >
            <PropertyLoyaltyPackageUpgradeDebug
              rate={rate}
              packageUpgradeTargetRate={loyaltyTargetUpgradeRate}
            />
            {cancellationPolicyContentOverride || <Group direction="vertical" gap={4} noDisplayWhenEmpty>
              {cancellationPolicy}
              {staffRateLabel && (
                <BodyText variant="medium" colour="light-foreground-alt">
                  {staffRateLabel}
                </BodyText>
              )}
            </Group>}
            {rate.promotion && <PropertyPromotionDetails promotion={rate.promotion} />}
            {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
            <RefundabilityInclusionsGap enabled={!!cancellationPolicyContentOverride}>
              <PropertyRateInclusions
                property={property}
                inclusions={visibleRateInclusions}
                bonusInclusions={visibleRateBonusInclusions}
                luxPlusInclusions={rate.luxPlusInclusions}
                bundleInclusions={visibleRateBundleInclusions}
                passportOffer={passportOffer}
                maxHeight={208}
                upgradeTargetRate={inclusionsUpgradeTargetRate}
                upgradeApplied={isLoyaltyPackageUpgradeApplied}
              />
              {shouldShowInclusionsValidityText && (
                <PackageInclusionsValidityText policyDescription={matchingPackage?.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
              )}
              {optionalBundlesSection}
            </RefundabilityInclusionsGap>
          </AccommodationSameRoomOptionTileDetails>

          <AccommodationSameRoomOptionTilePricing>
            {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <Group direction="horizontal" gap={4} wrap="wrap" noDisplayWhenEmpty>
              {lpcDiscount > 0 && <PriceRowValueDiscount
                type="estimate"
                discountPercentage={lpcDiscount}
              />}
              {!!rate.paymentScheduleInfo && <PayLaterPill />}
              {payAtPropertyDetails.shouldShowPill && <PayAtPropertyPill hasMultipleOptions={payAtPropertyDetails.shouldShowModal} />}
            </Group>}
            {rate.includesFlights && rate.cheapestFlightPrice === 'loading' && <LoadingIndicator floating opaque />}
            {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <VerticalSpacer gap={12}>
              {showAgentExclusivePackageLabel && <LabelGroup>
                <AgentExclusivePackageLabel />
              </LabelGroup>}
              <div>
                {showPointsSavings && <OfferPagePaidEntirelyWithPointsLabel />}
                <Caption variant="medium">
                  <Group direction="vertical" gap={4}>
                    <div>
                      <PriceRowPriceCaption startIcon={rate.includesFlights ? <LinePlaneDepartureIcon size="S" /> : undefined}>
                        {lpcDurationLabel}
                      </PriceRowPriceCaption>
                      {showPointsPrice && <OfferPageBurnPriceDisplay
                        totalPrice={rate.totals.displayPrice}
                        pointsToApply={burnPointsToApply}
                        priceInPoints={burnPriceInPoints}
                        pointsHideZeroCash={burnHideZeroCash}
                        memberPrice={rate.luxPlusTotals?.displayPrice}
                        size="M"
                        saleUnit="total"
                      />}
                      {!showPointsPrice && <PropertyRateOptionPrice
                        rate={rate}
                        selectedBundles={selectedOptionalBundles}
                        isMetasearchDiscountEligibleRate={isMetasearchDiscountEligibleRate}
                      />}
                      {shouldShowValue && <PriceRowValueDiscountWithCaption
                        size="M"
                        originalValue={rate.totals.valuedUpToPrice + selectedOptionalBundlesTotalPrice}
                      />}
                      {taxesAndFeesContent}
                    </div>
                    {rate.exceedsIncludedGuests && <AdditionalGuestsPopup
                      complex={false}
                      description={intl.formatMessage({
                        id: '3KcTGG2Q7zKoXLtwouAC',
                        defaultMessage: '+ Extra guest surcharge',
                        description: 'Description for additional guests popup in property rate option',
                      })}
                      modalContent={rate.includedGuestsExceededPolicy}
                    />}
                    {shouldShowMargin && <PriceRowMarginText
                      marginValue={rate.totals.marginAud!}
                      margin={rate.totals.margin}
                      type={rate.includesFlights ? 'property_estimate_accom' : 'property_estimate'}
                    />}
                  </Group>
                </Caption>
                {showReserveForZero && <TextLink
                  colour="highlight-primary-dark"
                  size="small"
                  weight="bold"
                  endIcon={<LineInfoCircleIcon colour="primary" />}
                  onClick={openReserveForZeroPolicyModal}
                >
                  {intl.formatMessage({
                    id: '3wQ4yMmF7-2EH4zxp8u0',
                    defaultMessage: 'Reserve now for free',
                    description: 'Hyperlink text to reserve now for free on the offer detail page',
                  })}
                </TextLink>}
              </div>
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={rate.includesFlights ? 'estimate' : 'exact'}
                pointsToApply={burnPointsToApply}
                burnPriceInPoints={burnPriceInPoints}
              />
            </VerticalSpacer>}
            {rate.includesFlights && (typeof rate.cheapestFlightPrice === 'string' || rate.cheapestFlightPrice === 0) && <VerticalSpacer gap={12}>
              {(rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable') && <BodyText colour="light-foreground-alt" variant="small">
                {noFlightsAvailableMessage}
              </BodyText>}
              {rate.cheapestFlightPrice === 'departureTooEarly' && <BodyText colour="light-foreground-alt" variant="small">
                {flightsWithin24HoursMessage}
              </BodyText>}
              {rate.cheapestFlightPrice === 'departureTooLate' && <BodyText colour="light-foreground-alt" variant="small">
                {flightsAdvanceBookingMessage}
              </BodyText>}
              {rate.cheapestFlightPrice === 0 && <BodyText colour="light-foreground-alt" variant="small">
                {addFlightsMessage}
              </BodyText>}
            </VerticalSpacer>}
            {/*
              * NB: Because we render a "plain" commission rate with a well-defined price, we don't
              * need to worry about aggregating min/max commission rates across all active offers
              * linked to this property.
              */}
            <PriceRowAgentHubCommission
              productType="tactical_ao_hotel"
              offerId={rate.service.offerId}
              packageId={rate.service.packageId}
              offerTotal={rate.totals.sellingPrice}
              kind="plain"
            />
          </AccommodationSameRoomOptionTilePricing>

          <AccommodationSameRoomOptionTileCTA>
            {ctas}
          </AccommodationSameRoomOptionTileCTA>
        </AccommodationSameRoomOptionTile>
      </AccommodationSameRoomOptionTileSubgrid>
    )
  }

  /**
   * BEDBANK RATES
   */
  if (rate.service.type === 'bedbank') {
    const bedbankDiscount = rawBedbankDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rawBedbankDiscount : 0

    const bedbankDurationLabel = rate.includesFlights
      ? intl.formatMessage(
          {
            id: 'qkFlsC5yUKDoPpbkWOvn',
            defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
            description: 'Duration with flights for bedbank property rate option',
          },
          { count: duration },
        )
      : intl.formatMessage(
          {
            id: '00Dl9U8_kbrsX9iSc4kT',
            defaultMessage: '{count, plural, one {# night} other {# nights}} from',
            description: 'Duration in nights for bedbank property rate option',
          },
          { count: duration },
        )

    return (
      <AccommodationSameRoomOptionTileSubgrid>
        {strongCalloutMessage && <SuggestedPackageBannerContainer>
          <PropertyPopularRateBanner message={strongCalloutMessage} />
        </SuggestedPackageBannerContainer>}
        <AccommodationSameRoomOptionTile
          ref={inViewRef}
          loyaltyUpgradeAppliedTier={isLoyaltyPackageUpgradeApplied ? tier : undefined}
        >
          {shouldShowTierProgressionBanner && highestUnlockableTier && (
            <AccommodationSameRoomOptionTileHeaderArea>
              <RateTierProgressionCallOutBanner
                highestUnlockableTier={highestUnlockableTier}
                highestUnlockableTierName={highestUnlockableTierName}
                onBookNow={handleBookNowFromModal}
                onAddToCart={handleAddToCartFromModal}
                checkoutDate={checkOut}
              />
            </AccommodationSameRoomOptionTileHeaderArea>
          )}
          {!!loyaltyUpgradeBanner && <AccommodationSameRoomOptionTileHeaderArea>
            {loyaltyUpgradeBanner}
          </AccommodationSameRoomOptionTileHeaderArea>}
          <AccommodationSameRoomOptionTileDetails
            optionName={rate.name}
            selectedLoyaltyTargetUpgrade={isLoyaltyPackageUpgradeApplied && loyaltyTargetUpgradeRate ? { name: loyaltyTargetUpgradeRate.name } : undefined}
            upgradeTitleVariant="packageUpgrade"
            tagLineLabel={<>
              {displayedContentRate.promotion && <PropertyPromotionLabel promotion={displayedContentRate.promotion} />}
            </>}
          >
            <PropertyLoyaltyPackageUpgradeDebug
              rate={rate}
              packageUpgradeTargetRate={loyaltyTargetUpgradeRate}
            />
            {cancellationPolicyContentOverride || <Group direction="vertical" gap={4} noDisplayWhenEmpty>
              {cancellationPolicy}
              {staffRateLabel && (
                <BodyText variant="medium" colour="light-foreground-alt">
                  {staffRateLabel}
                </BodyText>
              )}
            </Group>}
            {rate.promotion && <PropertyPromotionDetails promotion={rate.promotion} />}
            {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
            <RefundabilityInclusionsGap enabled={!!cancellationPolicyContentOverride}>
              <PropertyRateInclusions
                property={property}
                inclusions={rate.inclusions}
                bonusInclusions={rate.bonusInclusions}
                luxPlusInclusions={rate.luxPlusInclusions}
                bundleInclusions={rate.bundleInclusions}
                maxHeight={208}
                upgradeTargetRate={inclusionsUpgradeTargetRate}
                upgradeApplied={isLoyaltyPackageUpgradeApplied}
              />
              {optionalBundlesSection}
            </RefundabilityInclusionsGap>
            {/* For Bedbank offers we need to display exclusive extras outside of the regular inclusions section */}
            <ExperiencesPassportOfferSummary offer={passportOffer} />
          </AccommodationSameRoomOptionTileDetails>

          <AccommodationSameRoomOptionTilePricing>
            {(!rate.includesFlights ||
              (typeof rate.cheapestFlightPrice === 'number' &&
                rate.cheapestFlightPrice > 0)) && (
              <Group direction="horizontal" gap={4} noDisplayWhenEmpty>
                {bedbankDiscount > 0 && (
                  <PriceRowValueDiscount
                    type="estimate"
                    discountPercentage={bedbankDiscount}
                  />
                )}
                {!!rate.paymentScheduleInfo && <PayLaterPill />}
                {payAtPropertyDetails.shouldShowPill && <PayAtPropertyPill hasMultipleOptions={payAtPropertyDetails.shouldShowModal} />}
              </Group>
            )}

            {rate.includesFlights &&
              rate.cheapestFlightPrice === 'loading' && (
              <LoadingIndicator floating opaque />
            )}

            {(!rate.includesFlights ||
              (typeof rate.cheapestFlightPrice === 'number' &&
                rate.cheapestFlightPrice > 0)) && (
              <VerticalSpacer gap={12}>
                <div>
                  {showPointsSavings && <OfferPagePaidEntirelyWithPointsLabel />}
                  <PriceRowPriceCaption startIcon={rate.includesFlights ? <LinePlaneDepartureIcon size="S" /> : undefined}>
                    {bedbankDurationLabel}
                  </PriceRowPriceCaption>
                  {showPointsPrice && <OfferPageBurnPriceDisplay
                    totalPrice={displayPrice}
                    pointsToApply={burnPointsToApply}
                    priceInPoints={burnPriceInPoints}
                    pointsHideZeroCash={burnHideZeroCash}
                    memberPrice={displayMemberPrice}
                    size="M"
                    saleUnit="total"
                  />}
                  {!showPointsPrice && <PropertyRateOptionPrice
                    rate={rate}
                    selectedBundles={selectedOptionalBundles}
                    discountAmount={discountAmount}
                    isMetasearchDiscountEligibleRate={isMetasearchDiscountEligibleRate}
                  />}
                  <LoadingIndicator visible={discountFetching} floating opaque />

                  {!!rawBedbankDiscount && (
                    <PriceRowValueDiscountWithCaption
                      size="M"
                      originalValue={rate.totals.valuedUpToPrice + selectedOptionalBundlesTotalPrice}
                    />
                  )}

                  {taxesAndFeesContent}

                  {shouldShowMargin && (
                    <PriceRowMarginText
                      marginValue={rate.totals.marginAud!}
                      margin={rate.totals.margin}
                      supplier={rate.service.supplier}
                      type={
                        rate.includesFlights
                          ? 'property_estimate_accom'
                          : 'property_estimate'
                      }
                    />
                  )}
                </div>

                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={
                    rate.includesFlights ? 'estimate' : 'exact'
                  }
                  pointsToApply={burnPointsToApply}
                  burnPriceInPoints={burnPriceInPoints}
                />
              </VerticalSpacer>
            )}

            {rate.includesFlights &&
              (typeof rate.cheapestFlightPrice === 'string' ||
                rate.cheapestFlightPrice === 0) && (
              <VerticalSpacer gap={12}>
                {(rate.cheapestFlightPrice === 'originSameAsDestination' ||
                  rate.cheapestFlightPrice === 'unavailable') && (
                  <BodyText colour="light-foreground-alt" variant="small">
                    {noFlightsAvailableMessage}
                  </BodyText>
                )}

                {rate.cheapestFlightPrice === 'departureTooEarly' && (
                  <BodyText colour="light-foreground-alt" variant="small">
                    {flightsWithin24HoursMessage}
                  </BodyText>
                )}

                {rate.cheapestFlightPrice === 'departureTooLate' && (
                  <BodyText colour="light-foreground-alt" variant="small">
                    {flightsAdvanceBookingMessage}
                  </BodyText>
                )}

                {rate.cheapestFlightPrice === 0 && (
                  <BodyText colour="light-foreground-alt" variant="small">
                    {addFlightsMessage}
                  </BodyText>
                )}
              </VerticalSpacer>
            )}
            {/*
              * NB: Because we render a "plain" commission rate with a well-defined price, we don't
              * need to worry about aggregating min/max commission rates across all active offers
              * linked to this property.
              */}
            <PriceRowAgentHubCommission
              productType="bedbank_hotel"
              offerId={rate.service.propertyId}
              packageId={rate.service.rateId}
              offerTotal={rate.totals.sellingPrice}
              kind="plain"
              checkIn={checkIn}
              checkOut={checkOut}
              rooms={rooms}
              bedbankPackages={agentHubBedbankPackages}
            />
          </AccommodationSameRoomOptionTilePricing>

          <AccommodationSameRoomOptionTileCTA>
            {ctas}
          </AccommodationSameRoomOptionTileCTA>
        </AccommodationSameRoomOptionTile>
      </AccommodationSameRoomOptionTileSubgrid>
    )
  }

  return null
}

export default withOfferPageAndReduxState((state, appState) => {
  const propertyOfferMapping = getPropertyOfferMapping(state, appState)
  return {
    property: state.property!,
    isPropertyUltraLux: propertyOfferMapping?.isUltraLux ?? false,
    originAirport: state.airport,
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    duration: state.duration,
    rooms: state.rooms,
    regionCode: appState.geo.currentRegionCode,
    source: getSource(appState),
    utmContent: appState.router.location.query.utm_content,
    roomOption: state.roomOption,
    loyaltyTargetStayUpgradesSelected: state.loyaltyTargetStayUpgradesSelected,
    isLoggedIn: selectLoggedIn(appState),
    isSpoofed: appState.auth.account.isSpoofed,
    subscriber: getEnquirySubscriber(appState),
    canShowMargin: appState.auth.account.isSpoofed && appState.system.showMarginInSpoofMode,
    canShowStaffLabel: isLEStaff(appState) || appState.auth.account.isSpoofed,
    isInclusionsValidityEnabled: !!appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croInclusionValidity],
    cheapestHotelOnlyDiscountRate: getPropertyCheapestHotelOnlyRateForDiscount(state, appState),
    agentHubBedbankPackages: getAgentHubBedbankPackagesForProperty(state, appState),
    open: !!state.activeFilter,
  }
}, PropertyRateOption, preventRenderWithOpenFilter)
